// src/app/core/services/t0-simulation.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export type UpdateAction = 'rename' | 'delete';

export type T0SimulationParameters = {
  '% of reduction': number;
  '% of increase treatment': number;
  '% of increase diagnosis': number;
};

export interface CreateT0SimulationRequest {
  session_id: string;
  city: string;
  simulation_name: string;
  parameters: T0SimulationParameters;
}

export interface UpdateSimulationRequest {
  model: 't0';
  action: UpdateAction;
  session_id: string;
  city: string;
  old_simulation_name: string;
  new_simulation_name?: string;
}

export interface ApiStatusResponse {
  status: 'success' | 'fail';
  message?: string;
}

export interface RenameArgs {
  session_id: string;
  city: string;
  old_simulation_name: string;
  new_simulation_name: string;
}

export interface DeleteArgs {
  session_id: string;
  city: string;
  old_simulation_name: string;
}

@Injectable({ providedIn: 'root' })
export class T0SimulationService {
  // Keep as-is if you want, but note: this duplicates your auth interceptor.
  private readonly headers = new HttpHeaders({
    Authorization: `Bearer ${environment.handshakeToken}`,
    'Content-Type': 'application/json'
  });

  private readonly createUrl = `${environment.apiUrl}/simulation_t0`;
  private readonly updateUrl = `${environment.apiUrl}/update`;

  constructor(private http: HttpClient) {}

  /**
   * /simulation_t0 returns BASE64 JSON -> read as text and decode
   */
  async createSimulation(payload: CreateT0SimulationRequest): Promise<any> {
    try {
      const text = await firstValueFrom(
        this.http.post(this.createUrl, payload, { headers: this.headers, responseType: 'text' })
      );
      return this.decodeB64Json<any>(text);
    } catch (e) {
      throw this.decodeB64HttpError(e);
    }
  }

  /**
   * /update is plain JSON (keep default)
   */
  renameSimulation(args: RenameArgs): Promise<ApiStatusResponse> {
    const body: UpdateSimulationRequest = {
      model: 't0',
      action: 'rename',
      session_id: args.session_id,
      city: args.city,
      old_simulation_name: args.old_simulation_name,
      new_simulation_name: args.new_simulation_name
    };
    return firstValueFrom(this.http.post<ApiStatusResponse>(this.updateUrl, body, { headers: this.headers }));
  }

  /**
   * /update is plain JSON (keep default)
   */
  deleteSimulation(args: DeleteArgs): Promise<ApiStatusResponse> {
    const body: UpdateSimulationRequest = {
      model: 't0',
      action: 'delete',
      session_id: args.session_id,
      city: args.city,
      old_simulation_name: args.old_simulation_name
    };
    return firstValueFrom(this.http.post<ApiStatusResponse>(this.updateUrl, body, { headers: this.headers }));
  }

  private decodeB64Json<T>(b64: string): T {
    // base64 -> json
    const json = atob((b64 || '').trim());
    return JSON.parse(json) as T;
  }

  private decodeB64HttpError(err: any): any {
    // If backend returns base64 even for error bodies, decode it into a normal Error
    const e = err as HttpErrorResponse;

    try {
      if (typeof e?.error === 'string') {
        const decoded = this.decodeB64Json<any>(e.error);
        const msg = decoded?.message || decoded?.error || 'Request failed.';
        return new Error(msg);
      }
    } catch {}

    // fallback
    const msg = e?.message || 'Request failed.';
    return new Error(msg);
  }
}